/**
 * sheetModel — pure workbook structure + (de)serialization for the Sheets app.
 *
 * A workbook is stored in a WebtopFile of type "textfile" whose name ends in
 * ".sheet" and whose `content` is the JSON below. This reuses the whole existing
 * file system (drives, save/load, Recycle Bin) with no backend change.
 *
 *   workbook = {
 *     version, rows, cols, active,
 *     sheets: [{ name, cells: { "A1": "=B1*2" }, formats: { "A1": { b,i,a } } }]
 *   }
 *   cells   — address -> raw string the user typed ("5", "text", "=SUM(A1:A2)")
 *   formats — address -> { b: bold, i: italic, a: 'left'|'center'|'right' }
 */
export const DEFAULT_ROWS = 50;
export const DEFAULT_COLS = 26;
export const SHEET_EXT = ".sheet";

export const isSheetName = (name) => String(name || "").toLowerCase().endsWith(SHEET_EXT);

export const createSheet = (name = "Sheet1") => ({
  name,
  cells: {},
  formats: {},
  charts: [],
  merges: [], // [{ r0, c0, r1, c1 }] merged ranges
  condRules: [], // [{ range, op:'gt'|'lt'|'eq'|'contains', value, bg, fg }]
  notes: {}, // address -> comment text
});

// View / workbook-level settings (toggled from the View & Page Layout ribbons).
export const defaultView = () => ({
  gridlines: true,
  headings: true,
  formulaBar: true,
  showFormulas: false,
  zoom: 100,
});

export const createWorkbook = () => ({
  version: 1,
  rows: DEFAULT_ROWS,
  cols: DEFAULT_COLS,
  active: 0,
  colWidths: {}, // colIndex -> px (overrides default)
  rowHeights: {}, // rowIndex -> px (overrides default)
  view: defaultView(),
  freeze: { rows: 0, cols: 0 }, // frozen panes
  protected: false, // Protect Sheet (read-only)
  sheets: [createSheet("Sheet1")],
});

export const serialize = (wb) => JSON.stringify(wb);

// Parse + sanitize a stored workbook; always returns a valid workbook (falls
// back to a blank one) so a corrupt file can never crash the app.
export const deserialize = (str) => {
  let wb;
  try {
    wb = JSON.parse(str || "");
  } catch (e) {
    return createWorkbook();
  }
  if (!wb || typeof wb !== "object" || !Array.isArray(wb.sheets) || wb.sheets.length === 0) {
    return createWorkbook();
  }
  const sheets = wb.sheets.map((s, i) => ({
    name: (s && s.name) || `Sheet${i + 1}`,
    cells: s && s.cells && typeof s.cells === "object" ? s.cells : {},
    formats: s && s.formats && typeof s.formats === "object" ? s.formats : {},
    charts: s && Array.isArray(s.charts) ? s.charts : [],
    merges: s && Array.isArray(s.merges) ? s.merges : [],
    condRules: s && Array.isArray(s.condRules) ? s.condRules : [],
    notes: s && s.notes && typeof s.notes === "object" ? s.notes : {},
  }));
  return {
    version: 1,
    rows: Math.max(DEFAULT_ROWS, Number(wb.rows) || DEFAULT_ROWS),
    cols: Math.max(DEFAULT_COLS, Number(wb.cols) || DEFAULT_COLS),
    active: Math.min(Math.max(0, Number(wb.active) || 0), sheets.length - 1),
    colWidths: wb.colWidths && typeof wb.colWidths === "object" ? wb.colWidths : {},
    rowHeights: wb.rowHeights && typeof wb.rowHeights === "object" ? wb.rowHeights : {},
    view: { ...defaultView(), ...(wb.view && typeof wb.view === "object" ? wb.view : {}) },
    freeze: wb.freeze && typeof wb.freeze === "object" ? { rows: wb.freeze.rows || 0, cols: wb.freeze.cols || 0 } : { rows: 0, cols: 0 },
    protected: !!wb.protected,
    sheets,
  };
};

export const emptySheetContent = () => serialize(createWorkbook());
